import { Loading } from "../app/Status";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  formatTicketNumber,
  HOLD_DURATION_LABELS,
  HOLD_DURATIONS,
  HOLD_REASON_LABELS,
  HOLD_REASONS,
  INBOX_FILTER_LABELS,
  INBOX_FILTERS,
  TICKET_CHANNEL_LABELS,
  TICKET_COMPONENT_LABELS,
  TICKET_KIND_LABELS,
  TICKET_STATUS_LABELS,
  type HoldDuration,
  type HoldReason,
  type InboxFilter,
  type InboxRow,
  type TicketComponent,
  type TicketDetail,
} from "@klantpunt/shared";
import { IconBell, IconChevronLeft, IconSearch } from "../app/icons";
import { Modal } from "../app/Modal";
import { initials } from "../app/ProfileMenu";
import { api } from "../lib/api";
import { useMe } from "../lib/auth";
import { useToast } from "../lib/toast";
import {
  errMsg,
  fmtDateTime,
  inboxKey,
  notificationsKey,
  ticketKey,
  useAgents,
  useInbox,
  useSupportNotifications,
  useTicket,
} from "../features/support/api";
import { AccountPanel } from "../features/support/AccountPanel";
import { ArticlePicker } from "../features/support/ArticlePicker";
import { browserName } from "../features/support/browser";
import { Thread } from "../features/support/Thread";
import { useSupportEvents } from "../features/support/useSupportEvents";
import "../features/support/support.css";

/** Alleen voor het supportteam; anderen krijgen "bestaat niet" (zoals platformbeheer). */
export function SupportGuard({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  if (!me?.support)
    return (
      <div className="center">
        <p>Deze pagina bestaat niet.</p>
      </div>
    );
  return <>{children}</>;
}

export function SupportInboxPage() {
  return (
    <SupportGuard>
      <Inbox />
    </SupportGuard>
  );
}

function statusChip(t: Pick<InboxRow, "status" | "holdReason">) {
  return (
    <span className={`chip sp-st sp-st-${t.status}`}>
      {t.status === "on_hold" && t.holdReason ? HOLD_REASON_LABELS[t.holdReason] : TICKET_STATUS_LABELS[t.status]}
    </span>
  );
}

function Inbox() {
  const { id } = useParams();
  const { data: me } = useMe();
  const [filter, setFilter] = useState<InboxFilter>("open");
  const [q, setQ] = useState("");
  const inbox = useInbox(filter, q.trim());
  useSupportEvents("support");
  const notes = useSupportNotifications();
  const unread = notes.data?.unread ?? 0;
  const rows = inbox.data?.rows ?? [];
  const counts = inbox.data?.counts;

  return (
    <div className={`page sp-inbox${id ? " sp-has-detail" : ""}`}>
      <div className="sp-listcol">
        <div className="sp-listhead">
          <p className="label">
            <Link to="/platform">Platformbeheer</Link> · Support
          </p>
          <div className="sp-titlebar">
            <h1 className="dot">Inbox</h1>
            {inbox.data && inbox.data.newCount > 0 && (
              <span className="sp-newpill" data-testid="inbox-new-count" aria-label={`${inbox.data.newCount} nieuw`}>
                {inbox.data.newCount}
              </span>
            )}
            <nav className="sp-tools" aria-label="Support">
              <Link to="/platform/helpcenter" className="sp-tool">
                Helpcenter
              </Link>
              <Link to="/platform/status" className="sp-tool">
                Status
              </Link>
              {me?.support === "support_admin" && (
                <Link to="/platform/support/team" className="sp-tool">
                  Team
                </Link>
              )}
              <Link
                to="/platform/support/meldingen"
                className="sp-tool sp-bell"
                aria-label={unread ? `Mijn meldingen, ${unread} ongelezen` : "Mijn meldingen"}
              >
                <IconBell size={18} aria-hidden="true" />
                {unread > 0 && (
                  <span className="sp-badge" data-testid="notify-unread">
                    {unread}
                  </span>
                )}
              </Link>
            </nav>
          </div>
        </div>
        <div className="sp-filters" role="group" aria-label="Filter">
          {INBOX_FILTERS.map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {INBOX_FILTER_LABELS[f]}
              {counts && <span className="sp-fcount">{counts[f]}</span>}
            </button>
          ))}
        </div>
        <label className="sp-search">
          <IconSearch size={18} aria-hidden="true" />
          <input
            type="search"
            placeholder="Zoek op nummer, onderwerp, naam of organisatie"
            aria-label="Zoeken in de inbox"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        {inbox.isPending && <Loading />}
        {!inbox.isPending && rows.length === 0 && <p className="muted sp-empty">Geen berichten.</p>}
        <ul className="sp-rows" aria-label="Berichten">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to={`/platform/support/${r.id}`}
                className={`sp-row${r.id === id ? " active" : ""}${r.status === "new" ? " sp-new" : ""}`}
                aria-current={r.id === id ? "page" : undefined}
              >
                <span className="avatar sp-ravatar" aria-hidden="true">
                  {initials({ name: r.requesterName, email: r.requesterName })}
                </span>
                <span className="sp-rmain">
                  <span className="sp-rtop">
                    <span className="sp-rname grow">
                      {r.requesterName}
                      {r.orgName ? <span className="muted"> · {r.orgName}</span> : null}
                    </span>
                    <time className="sp-rtime" dateTime={r.updatedAt}>
                      {fmtDateTime(r.updatedAt)}
                    </time>
                  </span>
                  <strong className="sp-subj">{r.subject}</strong>
                  <span className="sp-rbot">
                    {statusChip(r)}
                    <span className="sp-rmeta grow">
                      {formatTicketNumber(r.number)} · {TICKET_KIND_LABELS[r.kind]} ·{" "}
                      {r.assignee ? (r.assignee.name ?? r.assignee.email) : "Niet toegewezen"}
                    </span>
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="sp-detailcol">
        {id ? <TicketPane id={id} /> : <p className="muted sp-pick">Kies een bericht.</p>}
      </div>
    </div>
  );
}

function TicketPane({ id }: { id: string }) {
  const { data: me } = useMe();
  const q = useTicket(id);
  const agents = useAgents();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [holding, setHolding] = useState(false);
  const [params, setParams] = useSearchParams();
  const notes = useSupportNotifications();
  const took = useRef(false);
  const t = q.data;

  // "Neem op" uit de meldingsmail (§8.4): direct aan jezelf toewijzen.
  useEffect(() => {
    if (!t || !me || took.current || params.get("neemop") !== "1") return;
    took.current = true;
    setParams({}, { replace: true });
    if (t.assignee?.id !== me.user.id) void act("/assign", { userId: me.user.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, me, params]);

  // Meldingen over dit bericht zijn gelezen zodra je het opent.
  const unreadHere = (notes.data?.items ?? []).filter((n) => n.ticketId === id && !n.read).map((n) => n.id);
  useEffect(() => {
    if (!unreadHere.length) return;
    void api("/support/notifications/read", { method: "POST", body: { ids: unreadHere } })
      .then(() => qc.invalidateQueries({ queryKey: notificationsKey }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadHere.join(",")]);

  async function act(path: string, body?: unknown) {
    try {
      const d = await api<TicketDetail>(`/support/tickets/${id}${path}`, { method: "POST", body: body ?? {} });
      qc.setQueryData(ticketKey(id), d);
      void qc.invalidateQueries({ queryKey: inboxKey });
      return true;
    } catch (e) {
      toast(errMsg(e));
      return false;
    }
  }

  if (q.isPending) return <Loading />;
  if (!t) return <p className="muted">Dit bericht bestaat niet.</p>;
  const meId = me?.user.id;

  return (
    <article className="sp-ticket" aria-label={`Bericht ${formatTicketNumber(t.number)}`}>
      <button type="button" className="backlink show-mobile linkbtn" onClick={() => navigate("/platform/support")}>
        <IconChevronLeft size={18} aria-hidden="true" />
        Inbox
      </button>
      <header className="sp-thead">
        <p className="label">
          {formatTicketNumber(t.number)} · {TICKET_KIND_LABELS[t.kind]} · {TICKET_CHANNEL_LABELS[t.channel]}
        </p>
        <div className="titlerow">
          <h2 className="sp-title">{t.subject}</h2>
          {statusChip(t)}
        </div>
        <div className="sp-actions">
          {t.assignee?.id !== meId && (
            <button type="button" className="btn small primary" onClick={() => void act("/assign", { userId: meId })}>
              Neem op
            </button>
          )}
          <label className="sp-assign">
            <span className="sr-only">Toegewezen aan</span>
            <select
              value={t.assignee?.id ?? ""}
              onChange={(e) => void act("/assign", { userId: e.target.value || null })}
              aria-label="Toegewezen aan"
            >
              <option value="">Niet toegewezen</option>
              {(agents.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ?? a.email}
                  {a.available ? "" : " (afwezig)"}
                </option>
              ))}
              {t.assignee && !(agents.data ?? []).some((a) => a.id === t.assignee!.id) && (
                <option value={t.assignee.id}>{t.assignee.name ?? t.assignee.email}</option>
              )}
            </select>
          </label>
          {t.status !== "done" && (
            <>
              <button type="button" className="btn small" onClick={() => setHolding(true)}>
                In de wacht
              </button>
              <button type="button" className="btn small" onClick={() => void act("/done")}>
                Afronden
              </button>
            </>
          )}
          {t.status === "done" && (
            <button type="button" className="btn small" onClick={() => void act("/reopen")}>
              Heropenen
            </button>
          )}
        </div>
      </header>
      <div className="sp-body">
        <div className="sp-main">
          <Thread messages={t.messages} view="support" />
          <Composer ticket={t} onSent={(d) => qc.setQueryData(ticketKey(id), d)} />
        </div>
        <div className="sp-sidecol">
          <ContextPanel t={t} />
          <AccountPanel t={t} />
        </div>
      </div>
      {holding && (
        <HoldDialog
          onClose={() => setHolding(false)}
          onSave={async (b) => (await act("/hold", b)) && setHolding(false)}
        />
      )}
    </article>
  );
}

function Composer({ ticket, onSent }: { ticket: TicketDetail; onSent: (d: TicketDetail) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const agents = useAgents();
  const { data: me } = useMe();
  const [type, setType] = useState<"agent" | "note">("agent");
  const [body, setBody] = useState("");
  const [mentions, setMentions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const d = await api<TicketDetail>(`/support/tickets/${ticket.id}/messages`, {
        method: "POST",
        body: { type, body, mentions: type === "note" ? mentions : [] },
      });
      onSent(d);
      void qc.invalidateQueries({ queryKey: inboxKey });
      setBody("");
      setMentions([]);
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  const others = (agents.data ?? []).filter((a) => a.id !== me?.user.id);
  return (
    <form className={`sp-composer sp-composer-${type}`} onSubmit={send}>
      <div className="sp-ctabs" role="radiogroup" aria-label="Soort reactie">
        {(
          [
            ["agent", "Antwoord aan klant"],
            ["note", "Interne notitie"],
          ] as const
        ).map(([k, l]) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={type === k}
            className="sp-ctab"
            onClick={() => setType(k)}
          >
            {l}
          </button>
        ))}
      </div>
      <label className="field">
        <span className="sr-only">{type === "agent" ? "Antwoord" : "Notitie"}</span>
        <textarea
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={type === "agent" ? `Antwoord aan ${ticket.requesterName}…` : "Alleen zichtbaar voor support…"}
          maxLength={20000}
        />
      </label>
      {type === "note" && others.length > 0 && (
        <fieldset className="sp-mentions">
          <legend className="small muted">Collega noemen</legend>
          {others.map((a) => (
            <label key={a.id} className="check small">
              <input
                type="checkbox"
                checked={mentions.includes(a.id)}
                onChange={(e) =>
                  setMentions(e.target.checked ? [...mentions, a.id] : mentions.filter((m) => m !== a.id))
                }
              />
              @{a.name ?? a.email}
            </label>
          ))}
        </fieldset>
      )}
      <div className="sp-cfoot">
        <button type="button" className="btn small ghost" onClick={() => setPicking(true)}>
          Artikel invoegen
        </button>
        <button type="submit" className="btn small primary" disabled={busy || !body.trim()}>
          {type === "agent" ? "Versturen" : "Notitie opslaan"}
        </button>
      </div>
      {picking && (
        <ArticlePicker
          onClose={() => setPicking(false)}
          onPick={(a) => {
            setBody((b) => `${b.trimEnd()}${b.trim() ? "\n\n" : ""}${a.title}: ${a.url}\n`);
            setPicking(false);
          }}
        />
      )}
    </form>
  );
}

function ContextPanel({ t }: { t: TicketDetail }) {
  const c = t.context ?? {};
  const rows: [string, ReactNode][] = [
    [
      "Afzender",
      <>
        {t.requesterName}
        <br />
        <a href={`mailto:${t.requesterEmail}`} className="sp-mail">
          {t.requesterEmail}
        </a>
      </>,
    ],
    ["Organisatie", t.orgName ?? "Niet gekoppeld"],
    ["Kanaal", TICKET_CHANNEL_LABELS[t.channel]],
    ...(t.component
      ? ([["Onderdeel", TICKET_COMPONENT_LABELS[t.component as TicketComponent]]] as [string, string][])
      : []),
    ...(c.buildingName ? ([["Gebouw", c.buildingName]] as [string, string][]) : []),
    ...(c.floorName ? ([["Verdieping", c.floorName]] as [string, string][]) : []),
    ...(c.route ? ([["Scherm", c.route]] as [string, string][]) : []),
    ...(c.appVersion ? ([["Versie", c.appVersion]] as [string, string][]) : []),
    ...(c.online !== undefined && c.online !== null
      ? ([["Verbinding", c.online ? "Online" : "Offline"]] as [string, string][])
      : []),
    ...(c.userAgent
      ? ([["Browser", <span title={c.userAgent}>{browserName(c.userAgent)}</span>]] as [string, ReactNode][])
      : []),
    ["Ontvangen", fmtDateTime(t.createdAt)],
    ...(t.dueAt && !t.firstResponseAt ? ([["Reageren voor", fmtDateTime(t.dueAt)]] as [string, string][]) : []),
    ...(t.holdUntil ? ([["Terug in de inbox", fmtDateTime(t.holdUntil)]] as [string, string][]) : []),
  ];
  return (
    <aside className="sp-side panel" aria-label="Context">
      <h3>Context</h3>
      <dl className="sp-facts">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </aside>
  );
}

function HoldDialog({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (b: { reason: HoldReason; duration: HoldDuration }) => Promise<unknown>;
}) {
  const [reason, setReason] = useState<HoldReason>("waiting_customer");
  const [duration, setDuration] = useState<HoldDuration>("until_reply");
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title="In de wacht zetten"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await onSave({ reason, duration });
              setBusy(false);
            }}
          >
            In de wacht
          </button>
        </>
      }
    >
      <label className="field">
        <span>Reden</span>
        <select value={reason} onChange={(e) => setReason(e.target.value as HoldReason)}>
          {HOLD_REASONS.map((r) => (
            <option key={r} value={r}>
              {HOLD_REASON_LABELS[r]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Terug in de inbox</span>
        <select value={duration} onChange={(e) => setDuration(e.target.value as HoldDuration)}>
          {HOLD_DURATIONS.map((d) => (
            <option key={d} value={d}>
              {HOLD_DURATION_LABELS[d]}
            </option>
          ))}
        </select>
      </label>
      <p className="hint">
        Reageert de klant eerder, dan komt het bericht meteen terug.
        {reason === "waiting_customer" ? " De klant ziet: Wacht op jou." : " De klant ziet: In behandeling."}
      </p>
    </Modal>
  );
}
